/* global Word */

// ═══════════════════════════════════════════════════════════════════════════
// Shared Utilities — used across multiple run functions
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Structured result detail — passed to reportResult() by each run.
 * Shown as an expandable dropdown in the QC Summary panel.
 */
export interface RunDetail {
  label: string;
  count?: number;
  note?: string;
  /** Stable paragraph ID for paragraph-level Go To */
  paraId?: string;
  /** Bookmark name for exact range-level Go To */
  bookmarkId?: string;
}

export const SRS_NAV_PREFIX = "_SRS_NAV_";
export const MAX_NAV_ITEMS = 50;

export function paraDetail(paraId: string, label: string, note?: string): RunDetail {
  const snippet = (label || "").trim().replace(/\s+/g, " ").substring(0, 60);
  return { label: snippet || "(paragraph)", paraId, note };
}

export function rangeDetail(bookmarkId: string, label: string, note?: string): RunDetail {
  const snippet = (label || "").trim().replace(/\s+/g, " ").substring(0, 60);
  return { label: snippet || "(range)", bookmarkId, note };
}

// ─── Callbacks ───────────────────────────────────────────────────────────

let _safeAlertCallback: ((msg: string, details?: RunDetail[]) => void) | null = null;

export function setSafeAlertCallback(cb: (msg: string, details?: RunDetail[]) => void): void {
  _safeAlertCallback = cb;
}

/** Legacy single-message alert. Still works — details will be empty. */
export function safeAlert(msg: string): void {
  if (_safeAlertCallback) _safeAlertCallback(msg, []);
  // S2486: handle or rethrow — log the error instead of silently swallowing it
  try {
    alert(msg);
  } catch (e) {
    console.log("ALERT: " + msg);
    console.warn("safeAlert: native alert() failed:", (e as Error).message);
  }
}

/** Send message + structured detail rows to the QC summary panel. */
export function reportResult(msg: string, details: RunDetail[]): void {
  if (_safeAlertCallback) _safeAlertCallback(msg, details);
  console.log("RESULT: " + msg);
  if (details.length > 0) {
    details.forEach(d => {
      const countPart = d.count === undefined ? '' : ': ' + d.count;
      const notePart = d.note ? ' (' + d.note + ')' : '';
      console.log(`  \u2022 ${d.label}${countPart}${notePart}`);
    });
  }
}

// ── Bullet characters shared between run1 and run7 ──
export const BULLET_CHAR_SET = new Set([
  '\u2022', '\u25CB', '\u25AA', '\u25AB', '\u25A0', '\u25A1',
  '\u25C6', '\u25C7', '\u27A2', '\u27A3', '\u2B9A',
  '\u00B7', '\u2219', '\uF0B7', '\uF0A7', '\uF076', '\uF0D8',
  '\uF0FC', '\uF0E8', '\uF0A8', '\uF071', '\uF06C', '\uF06E',
  '\u25CF', '\u25B6', '\u25C0', '\u25BA', '\u25C4', '\u2666',
  '\u2605', '\u2713', '\u2714', '\u2717', '\u2718', '\u27A4',
  '\u2023', '\u2043', '\u204D'
]);

export const BULLET_CHARS_ARRAY = [
  '\u2022', '\u25CB', '\u25AA', '\u25AB', '\u25A0', '\u25A1',
  '\u25C6', '\u25C7', '\u27A2', '\u27A3', '\u2B9A',
  '\u00B7', '\u2219', '\uF0B7'
];

export const SYMBOL_FONTS = [
  'symbol', 'wingdings', 'wingdings 2', 'wingdings 3', 'webdings',
  'zapf dingbats', 'zapfdingbats', 'mt extra', 'bookshelf symbol',
  'ms reference specialty', 'marlett'
];

export function isSymbolFont(fontName: string): boolean {
  return !!fontName && SYMBOL_FONTS.includes(fontName.toLowerCase().trim());
}

export async function ensureStyleExists(
  context: Word.RequestContext,
  targetStyle: string,
  verifiedCache: Set<string>
): Promise<boolean> {
  if (verifiedCache.has(targetStyle)) return false;
  const styles = context.document.getStyles();
  const styleCheck = styles.getByNameOrNullObject(targetStyle);
  styleCheck.load("isNullObject");
  await context.sync();
  let created = false;
  if (styleCheck.isNullObject) {
    console.log(`  Style "${targetStyle}" not found \u2014 creating...`);
    context.document.addStyle(targetStyle, Word.StyleType.paragraph);
    await context.sync();
    created = true;
    console.log(`  \u2713 Style "${targetStyle}" created`);
  }
  verifiedCache.add(targetStyle);
  return created;
}

// ═══════════════════════════════════════════════════════════════════════════
// Issue flagging (comment + QC summary entry)
// ═══════════════════════════════════════════════════════════════════════════

export async function flagIssue(
  para: Word.Paragraph,
  paraText: string,
  paraId: string,
  commentText: string,
  qcNote: string,
  flagged: RunDetail[],
  context: Word.RequestContext
): Promise<boolean> {
  try {
    const range = para.getRange(Word.RangeLocation.content);
    range.insertComment(commentText);
    await context.sync();
    flagged.push(paraDetail(paraId, paraText, qcNote));
    return true;
  } catch (e) {
    console.warn("  flagIssue: insertComment failed:", (e as Error).message);
    flagged.push(paraDetail(paraId, paraText, qcNote + " (comment insertion failed)"));
    return false;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// List-group flagger — one comment per contiguous list, not per item
// ═══════════════════════════════════════════════════════════════════════════

export interface ListGroupItem {
  kind: string;
  para: Word.Paragraph;
  paraId: string;
  paraText: string;
  currentStyle: string;
  expectedStyle: string;
  level: number;
  marker: string;
  detail?: string;
  contextLabel: string;
  headerLabel: string;
}

export class ListGroupFlagger {
  private current: ListGroupItem[] = [];
  // S2933: mark fields that are never reassigned as readonly
  private readonly context: Word.RequestContext;
  private readonly flagged: RunDetail[];
  private groupsEmitted = 0;
  private itemsCovered = 0;

  constructor(context: Word.RequestContext, flagged: RunDetail[]) {
    this.context = context;
    this.flagged = flagged;
  }

  async add(item: ListGroupItem): Promise<void> {
    if (this.current.length > 0 && this.current[0].kind !== item.kind) {
      await this.flush();
    }
    this.current.push(item);
  }

  // S3776: flush() refactored — delegate to private helpers to reduce
  // cognitive complexity from 27 down to well within the 15-unit limit.
  async flush(): Promise<void> {
    if (this.current.length === 0) return;

    const group = this.current;
    this.current = [];

    const commentText = this.buildCommentText(group);
    const note = this.buildQcNote(group);

    await this.insertGroupComment(group, commentText);

    const first = group[0];
    const count = group.length;
    const labelText = count === 1
      ? first.paraText
      : `List of ${count}: ${first.paraText}`;

    this.flagged.push(paraDetail(first.paraId, labelText, note));

    this.groupsEmitted++;
    this.itemsCovered += count;
  }

  /** Build the in-document comment body for a flushed group. */
  private buildCommentText(group: ListGroupItem[]): string {
    const first = group[0];
    const count = group.length;

    if (count === 1) {
      return this.buildSingleItemComment(first);
    }
    return this.buildMultiItemComment(group, first, count);
  }

  private buildSingleItemComment(item: ListGroupItem): string {
    const markerLine = item.marker ? `Marker: "${item.marker}"\n` : '';
    const detailLine = item.detail ? `${item.detail}\n` : '';
    return (
      `[QC] ${item.headerLabel} (${item.contextLabel}).\n` +
      `Currently: "${item.currentStyle}"\n` +
      `Expected: "${item.expectedStyle}"\n` +
      markerLine +
      `Level: ${item.level}\n` +
      detailLine +
      `(Auto-correction is disabled — please update the style manually.)`
    );
  }

  private buildMultiItemComment(
    group: ListGroupItem[],
    first: ListGroupItem,
    count: number
  ): string {
    // S4624: extract deduplicated style lists before interpolation to avoid
    // nested template literals inside the final template string.
    const seenCurrent = this.dedupeStyles(group, "currentStyle");
    const seenExpected = this.dedupeStyles(group, "expectedStyle");

    const currentStyleList = seenCurrent.map(s => `"${s}"`).join(', ');
    const expectedStyleList = seenExpected.map(s => `"${s}"`).join(', ');
    const expectedLabel = seenExpected.length === 1 ? 'Expected style' : 'Expected styles';
    const itemWord = count === 1 ? 'item' : 'items';
    const detailLine = first.detail ? `${first.detail}\n` : '';

    return (
      `[QC] ${first.headerLabel} (${first.contextLabel}).\n` +
      `This list has ${count} ${itemWord} flagged.\n` +
      `Currently styled as: ${currentStyleList}\n` +
      `${expectedLabel}: ${expectedStyleList}\n` +
      detailLine +
      `(Auto-correction is disabled — please update the styles manually.)`
    );
  }

  /** Collect unique values of `key` from group items, preserving order. */
  private dedupeStyles(
    group: ListGroupItem[],
    key: "currentStyle" | "expectedStyle"
  ): string[] {
    const seen: string[] = [];
    for (const it of group) {
      if (!seen.includes(it[key])) seen.push(it[key]);
    }
    return seen;
  }

  /** Build the short QC summary note for the group. */
  private buildQcNote(group: ListGroupItem[]): string {
    const first = group[0];
    const count = group.length;
    if (count === 1) {
      return `should be "${first.expectedStyle}" — currently "${first.currentStyle}"`;
    }
    return `${count} items — should be "${first.expectedStyle}" (list)`;
  }

  /** Insert one comment anchored on the group's range; swallow Word errors. */
  private async insertGroupComment(
    group: ListGroupItem[],
    commentText: string
  ): Promise<void> {
    try {
      const first = group[0];
      const firstRange = first.para.getRange(Word.RangeLocation.content);
      const commentRange = group.length > 1
        ? firstRange.expandTo(group[group.length - 1].para.getRange(Word.RangeLocation.content))
        : firstRange;

      commentRange.insertComment(commentText);
      await this.context.sync();
    } catch (e) {
      console.warn("  ListGroupFlagger: insertComment failed:", (e as Error).message);
    }
  }

  getStats(): { groupsEmitted: number; itemsCovered: number } {
    return { groupsEmitted: this.groupsEmitted, itemsCovered: this.itemsCovered };
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Default paragraph format helper
// ═══════════════════════════════════════════════════════════════════════════

// S3776: applydefaultparagraphformat refactored — split into focused helpers
// to reduce cognitive complexity from 40 down to well within the 15-unit limit.

/** A range to re-style, plus any explicit colour that must survive the change. */
interface StyleTarget {
  range: Word.Range;
  color: string | null;
}

export async function applydefaultparagraphformat(
  para: Word.Paragraph,
  styleName: string,
  context: Word.RequestContext,
  bodyText: boolean = false
): Promise<void> {
  const runs = await loadParagraphRuns(para, context);
  const { runsNeedingDrill, runsToStyle } = categoriseRuns(runs, styleName, bodyText);

  const directTargets: StyleTarget[] = runsToStyle.map(r => ({
    range: r.getRange(),
    color: preservableColor(r.font.color),
  }));
  await applyStyleKeepingColour(directTargets, styleName, context);

  await drillSuperSubRuns(runsNeedingDrill, styleName, context);
}

/** Load all text-range runs for a paragraph in one round-trip. */
async function loadParagraphRuns(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<Word.Range[]> {
  const runs = para.getTextRanges([" ", "\t", "\n"], true);
  runs.load("items");
  await context.sync();

  for (const r of runs.items) {
    r.load("style, text");
    r.font.load("superscript, subscript, color");
  }
  await context.sync();

  return runs.items;
}

/** Partition loaded runs into those that need drilling vs. direct re-styling. */
function categoriseRuns(
  runs: Word.Range[],
  styleName: string,
  bodyText: boolean
): { runsNeedingDrill: Word.Range[]; runsToStyle: Word.Range[] } {
  const runsNeedingDrill: Word.Range[] = [];
  const runsToStyle: Word.Range[] = [];

  for (const r of runs) {
    if (hasSuperOrSub(r)) {
      if (!bodyText) runsNeedingDrill.push(r);
    } else if (needsRestyle(r, styleName)) {
      runsToStyle.push(r);
    }
  }

  return { runsNeedingDrill, runsToStyle };
}

/** True when the run has (or might have) superscript or subscript set. */
function hasSuperOrSub(r: Word.Range): boolean {
  const sup = r.font.superscript;
  const sub = r.font.subscript;
  return sup === true || sup === null || sub === true || sub === null;
}

/** True when the run's style differs from the target and is not undefined/null. */
function needsRestyle(r: Word.Range, styleName: string): boolean {
  return r.style !== undefined && r.style !== null && r.style !== styleName;
}

/**
 * Word returns font.color as "#RRGGBB"; an empty string or "Automatic" means
 * no explicit colour is set. Applying a linked style to a run resets its
 * colour to the style default (automatic → black), which would silently wipe
 * coloured footnote text. We remember any explicit, non-black colour so it can
 * be re-applied after the style change. Black / automatic runs are left alone —
 * they reset to black either way, so there is nothing to preserve.
 */
function preservableColor(color: string | null | undefined): string | null {
  if (!color) return null;
  const c = color.trim();
  if (c === "" || c.toLowerCase() === "automatic") return null;
  if (c === "#000000" || c === "#000") return null;
  return c;
}

/**
 * Apply `styleName` to every target range in one sync, then restore any
 * explicit colour the style change stripped (a second sync runs only if at
 * least one colour actually needs restoring).
 */
async function applyStyleKeepingColour(
  targets: StyleTarget[],
  styleName: string,
  context: Word.RequestContext
): Promise<void> {
  if (targets.length === 0) return;

  for (const t of targets) {
    t.range.style = styleName;
  }
  await context.sync();

  let colourRestored = false;
  for (const t of targets) {
    if (t.color) {
      t.range.font.color = t.color;
      colourRestored = true;
    }
  }
  if (colourRestored) await context.sync();
}

/** Drill into super/sub runs and restyle any non-super/sub characters. */
async function drillSuperSubRuns(
  runsNeedingDrill: Word.Range[],
  styleName: string,
  context: Word.RequestContext
): Promise<void> {
  if (runsNeedingDrill.length === 0) return;

  const charGroups = runsNeedingDrill.map(r => r.getTextRanges([], true));
  for (const cg of charGroups) cg.load("items");
  await context.sync();

  for (const cg of charGroups) {
    for (const ch of cg.items) {
      ch.load("style");
      ch.font.load("superscript, subscript, color");
    }
  }
  await context.sync();

  const charTargets = collectCharsToRestyle(charGroups, styleName);
  await applyStyleKeepingColour(charTargets, styleName, context);
}

/** Collect character ranges that are not super/sub and need a style change,
 *  capturing each one's explicit colour so it survives the re-style. */
function collectCharsToRestyle(
  charGroups: Word.RangeCollection[],
  styleName: string
): StyleTarget[] {
  const targets: StyleTarget[] = [];
  for (const cg of charGroups) {
    for (const ch of cg.items) {
      if (ch.font.superscript === true || ch.font.subscript === true) continue;
      if (needsRestyle(ch, styleName)) {
        targets.push({ range: ch.getRange(), color: preservableColor(ch.font.color) });
      }
    }
  }
  return targets;
}